<template>
  <v-list-item
    dense
    :value="assetType"
    active-class="deep-green--text text--accent-4"
    :class="{
      'editing-container': assetType.isEditing,
      'asset-type-list-item': !assetType.isEditing,
    }"
  >
    <template v-slot:default="{}">
      <v-list-item-content class="pa-0 pb-3 ma-0">
        <div v-if="assetType.isEditing">
          <v-row dense class="pb-0">
            <v-col cols="12" class="pb-0">
              <div
                v-if="!titleEditable"
                class="pull-left ml-1 mt-2 text-subtitle-1"
              >
                {{ assetType.title }}
              </div>
              <v-text-field
                v-if="titleEditable"
                v-model="assetType.title"
                placeholder="Enter an asset type title"
                size="50"
                hide-details
                class="mb-2"
              />
            </v-col>
          </v-row>
          <v-row dense class="pt-0">
            <v-col cols="6" class="pt-0">
              <v-select
                v-model="assetType.color"
                :items="colors"
                item-text="color"
                item-value="hex"
                label="Select A Color"
                single-line
                clearable
                hide-details
                class="pt-0"
              >
                <template v-slot:selection="{ item }">
                  <div
                    class="color-holder mr-4"
                    :style="{ 'background-color': item.hex }"
                  ></div>
                  {{ item.color }}
                </template>
                <template v-slot:item="{ item }">
                  <div
                    class="color-holder mr-4"
                    :style="{ 'background-color': item.hex }"
                  ></div>
                  {{ item.color }}
                </template>
              </v-select>
            </v-col>
            <v-col cols="6" class="pt-0">
              <v-select
                v-model="assetType.icon"
                :items="icons"
                item-text="text"
                item-value="value"
                label="Select An Icon"
                single-line
                clearable
                hide-details
                class="pt-0"
              >
                <template v-slot:selection="{ item }"
                  ><img
                    :src="getDisplayIcon(item)"
                    alt
                    height="24"
                    class="icon mr-4"
                  />
                  {{ item.text }}
                </template>
                <template v-slot:item="{ item }">
                  <img
                    :src="getDisplayIcon(item)"
                    alt
                    height="24"
                    class="icon mr-2"
                  />
                  {{ item.text }}
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>
        <div v-if="!assetType.isEditing">
          <AssetIcon
            :icon="assetType.icon"
            :color="assetType.color"
            :icon-size="18"
            :container-size="32"
            class="pull-left"
          ></AssetIcon>
          <div class="pull-left ml-1 mt-2 text-subtitle-1">
            {{ assetType.title }}
          </div>
          <div class="clearfix"></div>
        </div>
      </v-list-item-content>

      <div class="mt-2">
        <div class="pull-left">
          <v-icon
            small
            class="mr-3 mt-4"
            v-if="!assetType.isEditing"
            v-on:click.prevent="handleClickEdit"
            >mdi-pencil</v-icon
          >
        </div>

        <v-switch
          v-model="assetType.enabled"
          class="pull-left pa-0 ma-0 mt-3"
          solo
          v-if="!assetType.isEditing"
          v-on:change="handleClickSave"
          :disabled="assetType.isDraft"
        ></v-switch>
        <div class="pull-right ml-3" v-if="assetType.isEditing">
          <v-btn
            small
            class="btn primary"
            :disabled="!readyToSave"
            @click.prevent="handleClickSave"
          >
            <Loader
              v-if="loadingSave"
              size="small"
              color="white"
              text=""
              class="pull-right mr-2"
            ></Loader>
            Save
          </v-btn>
          <v-btn
            text
            color="primary"
            small
            class="btn ml-2"
            @click.prevent="handleClickCancel"
          >
            Cancel
          </v-btn>
        </div>
        <div class="clearfix"></div>
      </div>
    </template>
  </v-list-item>
</template>

<script>
import lodashIsEmpty from "lodash/isEmpty";
import lodashCloneDeep from "lodash/cloneDeep";

import { MODULE_PREFIX_ASSETS } from "@/store";
import { SAVE_ASSET_TYPES, GET_ASSETS_REFRESH_MAP } from "@/store/actions.type";

import { ASSET_COLORS, ASSET_ICONS } from "@/common/constants/assetManagement";

const EMITTED_ACTIONS = {
  EDIT: "edit",
  SAVE: "save",
  CANCEL: "cancel",
};

export default {
  name: "AssetsCustomTypeEditor",
  components: {
    AssetIcon: () => import("@/components/assetManagement/AssetIcon"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    assetType: {
      type: Object,
    },
    titleEditable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      originalAssetType: null,
      colors: ASSET_COLORS,
      icons: ASSET_ICONS,
      defaultIcon: "star",
      defaultColor: "#000000",
      loadingSave: false,
    };
  },
  computed: {
    icon() {
      return require(`@/assets/svgs/assetManagement/asset_${this.assetType.icon}.svg`);
    },
    readyToSave() {
      let title = null;
      if (this.assetType?.title) {
        title = this.assetType.title.trim();
      }
      return !this.loadingSave && title;
    },
  },
  methods: {
    getDisplayIcon(item) {
      return require(`@/assets/svgs/assetManagement/asset_${item.value}.svg`);
    },
    handleClickEdit() {
      this.$emit(EMITTED_ACTIONS.EDIT, this.assetType);
      this.originalAssetType = lodashCloneDeep(this.assetType);
    },
    async handleClickSave() {
      this.loadingSave = true;
      const assetType = lodashCloneDeep(this.assetType);
      if (lodashIsEmpty(assetType.color)) {
        assetType.color = this.defaultColor;
      }
      if (lodashIsEmpty(assetType.icon)) {
        assetType.icon = this.defaultIcon;
      }
      assetType.title = assetType.title.trim();
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + SAVE_ASSET_TYPES,
        assetType
      );
      // This does reload asset on map without any filters
      this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSETS_REFRESH_MAP,
        new Date().toISOString()
      );
      this.originalAssetType = null;
      this.$emit(EMITTED_ACTIONS.SAVE);
      this.loadingSave = false;
    },
    handleClickCancel() {
      this.$emit(EMITTED_ACTIONS.CANCEL, this.assetType);
    },
  },
};
</script>

<style>
.v-list-item.asset-type-list-item {
  height: 52px !important;
}
</style>
<style scoped>
.asset-type-title {
  position: relative;
  margin-top: 4px;
}
.color-holder {
  height: 24px;
  width: 24px;
  border-radius: 12px;
}
.editing-container {
  background-color: #f0f0f0 !important;
}
</style>
